import { StandardPagination } from '@brandfolder/react';
import { t, Trans } from '@lingui/macro';
import classnames from 'classnames';
import React, {
  FunctionComponent,
  useEffect,
  useState,
} from 'react';

import { ApiKeyServerStatus, getApiKeys } from '@api/v4/organizations/api_keys';

import { formatDate, mapApiKey } from './apiKeysTableHelpers';
import { SortableHeader } from './SortableHeader';
import { StatusChip } from './StatusChip';
import { StatusFilterHeader } from './StatusFilterHeader';
import classes from './styles/api-keys-table.module.scss';
import { ApiKeyRow, ApiKeySortColumn, ApiKeySortState, ApiKeyStatus } from './types';

interface ApiKeysTableProps {
  organizationKey: string;
  organizationSlug: string;
}

const ROWS_PER_PAGE = 25;
const MAX_API_KEYS = 100;
const DEFAULT_SORT: ApiKeySortState = { column: 'createdAt', direction: 'desc' };

// Maps UI sort columns onto the server's sortable attribute names.
const SORT_FIELD: Record<ApiKeySortColumn, string> = {
  createdAt: 'created_at',
  expiresAt: 'expires_at',
  name: 'name',
  status: 'status',
};

// Maps the three UI chip states onto the server's status enum.
const SERVER_STATUS: Record<ApiKeyStatus, ApiKeyServerStatus> = {
  active: 'active',
  disabled: 'deactivated',
  expiring: 'expiring_soon',
};

export const ApiKeysTable: FunctionComponent<ApiKeysTableProps> = ({
  organizationKey,
}) => {
  const [sort, setSort] = useState<ApiKeySortState>(DEFAULT_SORT);
  const [statusFilter, setStatusFilter] = useState<ApiKeyStatus[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [apiKeys, setApiKeys] = useState<ApiKeyRow[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Sorting, filtering, and pagination are handled by the API: any change to
  // the sort, status filter, or page triggers a fresh request for that slice.
  useEffect(() => {
    let active = true;
    setLoading(true);
    setHasError(false);

    getApiKeys(organizationKey, {
      order: sort.direction,
      page: currentPage,
      per: ROWS_PER_PAGE,
      sort: SORT_FIELD[sort.column],
      status: statusFilter.map((status) => SERVER_STATUS[status]),
    })
      .then((response) => {
        if (active) {
          setApiKeys(response.data.map(mapApiKey));
          setTotalPages(Math.max(1, response.meta?.total_pages ?? 1));
          setTotalCount(response.meta?.total_count ?? response.data.length);
        }
      })
      .catch(() => {
        if (active) {
          setHasError(true);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return (): void => {
      active = false;
    };
  }, [organizationKey, sort, statusFilter, currentPage]);

  const visibleKeys = apiKeys;

  const handleSort = (column: ApiKeySortColumn): void => {
    setSort((previous) => {
      if (previous.column === column) {
        return { column, direction: previous.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: 'asc' };
    });
    setCurrentPage(1);
  };

  const handleStatusFilter = (statuses: ApiKeyStatus[]): void => {
    setStatusFilter(statuses);
    setCurrentPage(1);
  };

  // Dummy handlers — real behaviour (rotate/revoke modals) is handled in later tickets.
  const handleRotate = (): void => undefined;
  const handleRevoke = (): void => undefined;

  return (
    <div className={classes.apiKeysTable}>
      <div className={classes.toolbar}>
        <p className={classes.countLabel}>
          <Trans>
            {totalCount} / {MAX_API_KEYS} keys (consider removing unused keys)
          </Trans>
        </p>
        {totalPages > 1 && (
          <StandardPagination
            key={`${sort.column}-${sort.direction}-${statusFilter.join(',')}`}
            initial={currentPage}
            labels={{
              nextLabel: t`Next page`,
              previousLabel: t`Previous page`,
            }}
            onPageChange={setCurrentPage}
            total={totalPages}
          />
        )}
      </div>

      <table className={classes.table}>
        <colgroup>
          <col className={classes.colName} />
          <col className={classes.colStatus} />
          <col className={classes.colCreation} />
          <col className={classes.colExpiry} />
          <col className={classes.colSecret} />
          <col className={classes.colActions} />
        </colgroup>
        <thead>
          <tr>
            <SortableHeader column="name" onSort={handleSort} sort={sort}>
              <Trans>Key Name</Trans>
            </SortableHeader>
            <StatusFilterHeader onChange={handleStatusFilter} selected={statusFilter}>
              <Trans>Status</Trans>
            </StatusFilterHeader>
            <SortableHeader column="createdAt" onSort={handleSort} sort={sort}>
              <Trans>Creation</Trans>
            </SortableHeader>
            <SortableHeader column="expiresAt" onSort={handleSort} sort={sort}>
              <Trans>Expiry</Trans>
            </SortableHeader>
            <th className={classes.headerCell} scope="col"><Trans>Secret Code</Trans></th>
            <th className={classes.headerCell} scope="col">
              <span className={classes.srOnly}><Trans>Actions</Trans></span>
            </th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td className={classes.emptyCell} colSpan={6}>
                <Trans>Loading API Keys…</Trans>
              </td>
            </tr>
          )}
          {!loading && hasError && (
            <tr>
              <td className={classes.emptyCell} colSpan={6}>
                <Trans>We couldn’t load your API Keys. Please try again.</Trans>
              </td>
            </tr>
          )}
          {!loading && !hasError && visibleKeys.length === 0 && (
            <tr>
              <td className={classes.emptyCell} colSpan={6}>
                <Trans>No API Keys yet. Create one to get started.</Trans>
              </td>
            </tr>
          )}
          {!loading && !hasError && visibleKeys.map((apiKey) => (
            <tr
              key={apiKey.id}
              className={classnames({ [classes.disabledRow]: apiKey.status === 'disabled' })}
            >
              <td className={classes.nameCell} title={apiKey.description || apiKey.name}>
                {apiKey.name}
              </td>
              <td><StatusChip status={apiKey.status} /></td>
              <td>
                <div className={classes.creationCell}>
                  <span>{formatDate(apiKey.createdAt)}</span>
                  <span className={classes.createdBy}>{apiKey.createdBy}</span>
                </div>
              </td>
              <td>{formatDate(apiKey.expiresAt)}</td>
              <td>
                <div className={classes.secretCell}>
                  <span className={classes.secretLine}>
                    <span className={classes.tokenPrefix}>{apiKey.tokenPrefix}</span>
                    <span aria-hidden="true" className={classes.tokenDots}>&bull; &bull; &bull;</span>
                  </span>
                  {apiKey.rotatedFromTokenPrefix && (
                    <span className={classnames(classes.secretLine, classes.rotatedFromLine)}>
                      <span className={classes.tokenPrefix}>{apiKey.rotatedFromTokenPrefix}</span>
                      <span aria-hidden="true" className={classes.tokenDots}>&bull; &bull; &bull;</span>
                    </span>
                  )}
                </div>
              </td>
              <td>
                <div className={classes.actionsCell}>
                  <button
                    className={classes.rotateButton}
                    onClick={handleRotate}
                    type="button"
                  >
                    <Trans>Rotate</Trans>
                  </button>
                  <button
                    aria-label={t`Revoke ${apiKey.name}`}
                    className={classes.revokeButton}
                    onClick={handleRevoke}
                    type="button"
                  >
                    <Trans>Revoke</Trans>
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
